import { useNavigate, useSearchParams } from 'react-router-dom'
import { X } from 'lucide-react'
import { useVulnerabilities } from '@/hooks/queries'

/**
 * Phase B T11. Displays the current vendor filter on the
 * VulnerabilitiesPage when `?vendor=<uuid>` is present in the URL, with
 * an `×` affordance to clear the filter.
 *
 * Resolves the vendor display name lazily by piggy-backing on the
 * already-fetched filtered list — the first row's `topAffectedVendors`
 * always contains the active filter vendor (server-side filter
 * guarantee). Avoids a dedicated `/api/vendors/:id` route for v1; Phase
 * C may add one when an admin triage UI ships.
 */
interface VendorFilterChipProps {
  vendorId: string
}

export function VendorFilterChip({ vendorId }: VendorFilterChipProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Reuses the same hook the page uses, so React Query dedupes the
  // request — zero extra network round-trips.
  const { data: vulns } = useVulnerabilities({ vendor: vendorId })
  const firstRow = vulns?.[0]
  const resolved = firstRow?.topAffectedVendors?.find((v) => v.id === vendorId)
  const name = resolved?.name ?? 'this vendor'

  const remove = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('vendor')
    const qs = next.toString()
    navigate(qs ? `/vulnerabilities?${qs}` : '/vulnerabilities')
  }

  return (
    <div
      className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-sm"
      data-testid="vendor-filter-chip"
    >
      <span>
        Filtered by vendor: <strong>{name}</strong>
      </span>
      <button
        onClick={remove}
        aria-label="Remove vendor filter"
        className="ml-1 rounded hover:bg-secondary-foreground/10"
        type="button"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
